import React from 'react';
import PropTypes from 'prop-types';

import isEmpty from 'lodash/isEmpty';
import intersection from 'lodash/intersection';
import union from 'lodash/union';
import difference from 'lodash/difference';
import map from 'lodash/map';
import isEqual from 'lodash/isEqual';

import { gettext } from '@eventbrite/i18n';

import { DataTableList } from '@eventbrite/eds-data-table-list';
import { DataTableHeader } from '@eventbrite/eds-data-table-header';
import { DataTableActionBar } from '@eventbrite/eds-data-table-action-bar';
import { SelectField } from '@eventbrite/eds-input-field';
import { Pagination } from '@eventbrite/eds-pagination';
import * as lib from './lib';

import {
    ITEMS_PROP_TYPE,
    SELECTED_ITEMS_PROP_TYPE,
} from '@eventbrite/eds-data-table-list';
import { COLUMNS_PROP_TYPE } from '@eventbrite/eds-data-table-list-item';
import { TEXT_ITEMS_PROP_TYPE } from '@eventbrite/eds-text-list';
import {
    DISABLED_ITEMS_PROP_TYPE,
    HIGHLIGHTED_ITEMS_PROP_TYPE,
    PAGE_SIZES_PROP_TYPE,
} from './constants';
import { getSelectPropValues } from '@eventbrite/eds-select';

import './dataTable.scss';

const PageSizeSelect = ({ pageSize, pageSizes, itemCount, ...extraAttrs }) => {
    if (!pageSizes) {
        return null;
    }

    const minPageSize = Math.min(...pageSizes);

    // if there are fewer items than the smallest page size option, there's
    // no need to show the page sizes selector
    if (itemCount <= minPageSize) {
        return null;
    }

    const values = getSelectPropValues(pageSizes);
    const value = String(pageSize);

    return (
        <div className="eds-data-table__size-select">
            <SelectField
                values={values}
                value={value}
                label={gettext('Show')}
                {...extraAttrs}
            />
        </div>
    );
};

const DataTablePagination = ({ pageCount, onSelected, ...extraAttrs }) => (
    <Pagination pageCount={pageCount} onSelected={onSelected} {...extraAttrs} />
);

const DataTableFooter = ({
    items,
    page,
    totalItemCount,
    pageSize,
    pageSizes,
    onPageChange,
    onPageSizeChange,
}) => {
    const currentPageSize = lib.getPageSize(pageSize, pageSizes, items);
    const currentItemCount = totalItemCount || items.length;
    const pageCount = Math.ceil(currentItemCount / currentPageSize);

    // if there is no page change handler or if there's only the single page,
    // there's no need to show the pagination
    // Also verify that pageCount is not NaN ( 0/0 )
    if (Number.isNaN(pageCount) || pageCount <= 1 || !onPageChange) {
        return null;
    }

    return (
        <div className="eds-data-table__pagination eds-l-mar-top-4">
            <DataTablePagination
                pageNumber={page}
                pageCount={pageCount}
                pageSize={currentPageSize}
                onSelected={onPageChange}
            />
            <div className="eds-data-table__pagination-size">
                <PageSizeSelect
                    pageSizes={pageSizes}
                    pageSize={currentPageSize}
                    onChange={onPageSizeChange}
                    itemCount={currentItemCount}
                />
            </div>
        </div>
    );
};

export default class DataTable extends React.Component {
    static propTypes = {
        ...DataTableActionBar.propTypes,
        /**
         * Array of objects containing row information.
         * If `pageSize` is provided, only `pageSize` rows will be shown at any given time.
         * If `filterText` is provided, the rows will be filtered to only render those that
         * match the provided `filterText`
         **/
        items: ITEMS_PROP_TYPE.isRequired,
        /**
         * List of objects of column values
         * ex. [{display: 'Email Address', value: 'email' }, {display: 'Date of Birth', value:'dob'}]
         */
        columns: COLUMNS_PROP_TYPE.isRequired,
        /**
         * Whether or not all row items are should be selected on initial render
         **/
        defaultSelectAll: PropTypes.bool,
        /**
         * Array of item ids to be selected by default. You can also pass this to reset selected items while the table is mounted.
         **/
        defaultSelectedItems: SELECTED_ITEMS_PROP_TYPE,
        /**
         * Array of items ID to disable each row
         **/
        disabledItems: DISABLED_ITEMS_PROP_TYPE,
        /**
         * Array of items ID to highlight each row
         **/
        highlightedItems: HIGHLIGHTED_ITEMS_PROP_TYPE,
        /**
         * List of available row actions
         */
        itemActions: TEXT_ITEMS_PROP_TYPE,
        /**
         * This function will optionally filter out actions based on action and item
         */
        filterItemAction: PropTypes.func,
        /**
         * Callback to be invoked on item action
         * (action, payload) => { if (action === 'delete') { ... } }
         **/
        onItemAction: PropTypes.func,
        /**
         * Callback to be invoked when page changes
         * (value) => { console.log(value) // 2 }
         **/
        onPageChange: PropTypes.func,
        /**
         * Callback to be invoked when page size changes
         * (value) => { console.log(value) // 25 }
         **/
        onPageSizeChange: PropTypes.func,
        /**
         * Callback to be invoked when select all checkbox changes
         * (selectedItemIds) => { console.log(selectedItemIds) // [{'name': 'Jon'}, {'name': 'Dany'}]}
         * The `selectedItemIds` argument that is returned includes all ids, not just those on the current page
         **/
        onSelectAllChange: PropTypes.func,
        /**
         * Callback to be invoked when sort column changes
         * (sortColumn, sortDescending) => { console.log(sortColumn) // 'name' }
         **/
        onSortChange: PropTypes.func,
        /**
         * Current page of the table
         **/
        page: PropTypes.number,
        /**
         * Current page size of the table
         **/
        pageSize: PropTypes.number,
        /**
         * Available page sizes, e.g. [25, 50, 100]
         **/
        pageSizes: PAGE_SIZES_PROP_TYPE,
        /**
         * Name of column from columns prop, e.g. 'name'
         **/
        sortColumn: PropTypes.string,
        /**
         * Whether or not row items are sorted in descending manner
         **/
        sortDescending: PropTypes.bool,
        /**
         * Total count of items, used for pagination display. This prop should only be used if
         * `items` does not reflect ALL of the items that can be shown. This allows for custom pagination.
         * By default, DataTable expects to receive all items, so if not provided the pagination display
         * will be based on the item count.
         **/
        totalItemCount: PropTypes.number,
        /**
         * Determines if the filter text box will display
         **/
        showTextFilter: PropTypes.bool,
        /**
         * A predicate function to filter items by based on the filterText. Takes the
         * following arguments: (filterText, item, filterableColumns) => {}
         * If none is passed in a default predicate will be used which observes the following rules:
         * coerces all values to string, case-insensitive, substring match
         **/
        filterPredicate: PropTypes.func,
        /**
         * The text to filter items by.
         **/
        filterText: PropTypes.string,
        /**
         * Disable filtering and sorting on the frontend. Trust that items are processed server-side.
         **/
        disableFilterAndSort: PropTypes.bool,
    };

    static defaultProps = {
        highlightedItems: [],
        sortDescending: false,
        defaultSelectAll: false,
        showTextFilter: false,
        page: 1,
        filterPredicate: lib.defaultFilterPredicate,
        filterText: '',
        disableFilterAndSort: false,
        disabledItems: [],
    };

    constructor(props) {
        super(props);

        const { defaultSelectAll, defaultSelectedItems, items } = props;
        const selectedItemIds = lib.getSelectedItemIds(
            defaultSelectAll,
            items,
            defaultSelectedItems,
        );

        this.state = {
            selectedItemIds,
        };
    }

    UNSAFE_componentWillReceiveProps(nextProps) {
        const { items, defaultSelectedItems } = this.props;
        const {
            items: nextItems,
            defaultSelectedItems: nextDefaultSelectedItems,
        } = nextProps;
        const newDefaultItemsAreDifferent = !isEqual(
            defaultSelectedItems,
            nextDefaultSelectedItems,
        );
        const nextItemsAreDifferent = !isEqual(nextItems, items);

        if (nextItemsAreDifferent || newDefaultItemsAreDifferent) {
            this.setState(() => ({
                selectedItemIds: intersection(
                    nextDefaultSelectedItems,
                    map(nextItems, 'id'),
                ),
            }));
        }
    }

    _getFilteredAndSortedItems() {
        const {
            items,
            columns,
            filterText,
            filterPredicate,
            sortDescending,
            sortColumn: currentSortColumn,
        } = this.props;

        return lib.getFilteredAndSortedItems({
            items,
            columns,
            filterText,
            filterPredicate,
            sortDescending,
            currentSortColumn,
        });
    }

    _getCurPageItemIds(filteredAndSortedItems) {
        const { page, pageSize } = this.props;
        const curPageItems = lib.getPaginatedItems(
            filteredAndSortedItems,
            page,
            pageSize,
        );

        return curPageItems.map((item) => item.id);
    }

    _handleSelectAllChange(checked) {
        const { onSelectAllChange, disabledItems } = this.props;
        const filteredAndSortItemIds = this._getFilteredAndSortedItems();
        const curPageItemIds = this._getCurPageItemIds(filteredAndSortItemIds);
        const enabledCurPageItemsIds = difference(
            curPageItemIds,
            disabledItems,
        );
        let { selectedItemIds } = this.state;

        if (checked) {
            selectedItemIds = union(enabledCurPageItemsIds, selectedItemIds);
        } else {
            selectedItemIds = difference(
                selectedItemIds,
                enabledCurPageItemsIds,
            );
        }

        this.setState({
            selectedItemIds,
        });

        if (onSelectAllChange) {
            onSelectAllChange(checked, selectedItemIds);
        }
    }

    _handleSelect(itemId, checked) {
        let { selectedItemIds } = this.state;

        selectedItemIds = lib.getUpdatedSelectedItems(
            itemId,
            selectedItemIds,
            checked,
        );

        this.setState({
            selectedItemIds,
        });
    }

    _handleSortChange(sortColumn, sortDescending) {
        const { onSortChange } = this.props;

        if (onSortChange) {
            onSortChange(sortColumn, sortDescending);
        }
    }

    _handleItemAction(action, itemId, value) {
        const { onItemAction } = this.props;

        const actionMap = {
            select: this._handleSelect.bind(this),
        };

        if (action in actionMap) {
            actionMap[action](itemId, value);
        }

        if (onItemAction) {
            onItemAction(action, itemId, value);
        }
    }

    _handleBulkActionSelect(value) {
        const { onBulkActionSelect } = this.props;
        const { selectedItemIds } = this.state;

        if (onBulkActionSelect) {
            onBulkActionSelect(value, selectedItemIds);
        }
    }

    _handlePageSizeChange(value) {
        const { onPageSizeChange } = this.props;

        if (onPageSizeChange) {
            onPageSizeChange(parseInt(value, 10));
        }
    }

    _renderDataActionBar(selectedItemIds) {
        const {
            buttonText,
            isButtonEnabled,
            isSelectFilterEnabled,
            onButtonClick,
            isBulkActionEnabled,
            showTextFilter,
            filterPlaceholder,
            filterText,
            bulkActions,
            selectFilterActions,
            onSelectFilterSelect,
            onTextFilterBlur,
            onTextFilterChange,
        } = this.props;

        if (!showTextFilter && !bulkActions) {
            return null;
        }

        let computedBulkActionEnabled = isBulkActionEnabled;

        if (computedBulkActionEnabled === undefined) {
            computedBulkActionEnabled = !isEmpty(selectedItemIds);
        }

        return (
            <DataTableActionBar
                bulkActions={bulkActions}
                selectFilterActions={selectFilterActions}
                filterPlaceholder={filterPlaceholder}
                filterText={filterText}
                hideTextFilter={!showTextFilter}
                buttonText={buttonText}
                isButtonEnabled={isButtonEnabled}
                isSelectFilterEnabled={isSelectFilterEnabled}
                onButtonClick={onButtonClick}
                isBulkActionEnabled={computedBulkActionEnabled}
                onBulkActionSelect={this._handleBulkActionSelect.bind(this)}
                onSelectFilterSelect={onSelectFilterSelect}
                onTextFilterBlur={onTextFilterBlur}
                onTextFilterChange={onTextFilterChange}
            />
        );
    }

    _renderDataTableContent(selectedItemIds, paginatedAndFilteredItems) {
        const {
            bulkActions,
            columns,
            disabledItems,
            highlightedItems,
            itemActions,
            filterItemAction,
            sortColumn,
            sortDescending,
            isWideColumn,
            noBorderForTableRow,
            isTableListItemHover,
        } = this.props;
        const unselectedDisabledItems = difference(
            disabledItems,
            selectedItemIds,
        );
        const curPageSelectedItems = paginatedAndFilteredItems.filter(
            ({ id }) =>
                selectedItemIds.includes(id) &&
                !unselectedDisabledItems.includes(id),
        );
        const paginatedAndFilteredSelectedItems =
            paginatedAndFilteredItems.filter(
                ({ id }) => !unselectedDisabledItems.includes(id),
            );
        const headerColumns = lib.getColumns(columns, itemActions);
        const isSelectAll =
            curPageSelectedItems.length ===
            paginatedAndFilteredSelectedItems.length;
        const isSelectable = !!bulkActions;

        return (
            <div className="eds-data-table__wrapper">
                <table
                    className="eds-data-table__main eds-l-mar-vert-2"
                    data-spec="data-table-main"
                >
                    <DataTableHeader
                        columns={headerColumns}
                        isSelected={isSelectAll}
                        isSelectable={isSelectable}
                        sortColumn={sortColumn}
                        sortDescending={sortDescending}
                        onSelect={this._handleSelectAllChange.bind(this)}
                        onSortChange={this._handleSortChange.bind(this)}
                    />
                    <DataTableList
                        highlightedItems={highlightedItems}
                        items={paginatedAndFilteredItems}
                        filterAction={filterItemAction}
                        isSelectable={isSelectable}
                        selectedItemIds={map(curPageSelectedItems, 'id')}
                        columns={columns}
                        actions={itemActions}
                        onItemAction={this._handleItemAction.bind(this)}
                        isTableListItemHover={isTableListItemHover}
                        disabledItems={disabledItems}
                        isWideColumn={isWideColumn}
                        noBorderForTableRow={noBorderForTableRow}
                    />
                </table>
            </div>
        );
    }

    render() {
        const {
            disableFilterAndSort,
            items,
            onPageChange,
            page,
            pageSize,
            pageSizes,
            totalItemCount,
        } = this.props;
        const { selectedItemIds } = this.state;
        const filteredAndSortedItems = disableFilterAndSort
            ? items
            : this._getFilteredAndSortedItems();
        const derivedTotalItemCount = totalItemCount || items.length;
        let paginatedItems = filteredAndSortedItems;

        if (pageSize < derivedTotalItemCount) {
            paginatedItems = lib.getPaginatedItems(
                filteredAndSortedItems,
                page,
                pageSize,
            );
        }

        const dataActionBar = this._renderDataActionBar(selectedItemIds);
        const dataTableContent = this._renderDataTableContent(
            selectedItemIds,
            paginatedItems,
        );

        return (
            <div className="eds-data-table">
                {dataActionBar}
                {dataTableContent}
                <DataTableFooter
                    items={paginatedItems}
                    page={page}
                    pageSize={pageSize}
                    pageSizes={pageSizes}
                    onPageChange={onPageChange}
                    onPageSizeChange={this._handlePageSizeChange.bind(this)}
                    totalItemCount={derivedTotalItemCount}
                />
            </div>
        );
    }
}
